import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, FileText } from "lucide-react";
import { useLang } from "@/i18n";
import { memberGetService, mediaUrl } from "@/lib/api";

const GOLD = "#C7A668";

export default function ReportView() {
  const { id } = useParams();
  const nav = useNavigate();
  const { L } = useLang();
  const [svc, setSvc] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    const t = localStorage.getItem("tb_member");
    if (!t) { nav("/portal"); return; }
    memberGetService(t, id).then(setSvc).catch(() => setErr(L("Report not available.", "Reporte no disponible.")));
  }, [id, nav, L]);

  if (err) return <Center>{err}</Center>;
  if (!svc) return <Center><div className="h-7 w-7 rounded-full border-2 border-white/15 animate-spin" style={{ borderTopColor: GOLD }} /></Center>;

  const media = svc.media || [];
  return (
    <div className="grain min-h-screen" style={{ background: "radial-gradient(120% 90% at 50% -10%, #14130f 0%, #0a0a0b 55%, #060708 100%)", color: "#F2EFE8" }}>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <button data-testid="report-back" onClick={() => nav(-1)} className="flex items-center gap-2 text-white/50 hover:text-white text-xs font-mono tracking-widest mb-8"><ArrowLeft size={14} />{L("BACK", "ATRÁS")}</button>

        <div className="font-mono mb-3" style={{ fontSize: 11, letterSpacing: ".24em", color: GOLD }}>{L("READINESS REPORT", "REPORTE DE READINESS")}</div>
        <h1 className="font-display" style={{ fontSize: "clamp(28px,4vw,44px)", letterSpacing: "-.02em" }}>{svc.title}</h1>
        <div className="text-white/50 font-light mt-2">{svc.aircraft?.model}{svc.aircraft?.tailNumber ? ` · ${svc.aircraft.tailNumber}` : ""} · {svc.date}</div>

        <div className="flex items-center gap-2.5 mt-5 flex-wrap">
          <span data-testid="report-status" className="font-mono text-[10px] tracking-widest px-3 py-1.5 rounded-sm" style={{ color: "#34d399", border: "1px solid #34d39955" }}>{(svc.status || "completed").replace(/_/g, " ").toUpperCase()}</span>
          {(svc.modules || []).map((m) => <span key={m} className="font-mono text-[10px] tracking-wide text-white/55 border border-white/12 px-2.5 py-1 rounded-sm">{m}</span>)}
        </div>

        {svc.summary && (
          <div className="mt-8">
            <div className="font-mono text-[10px] tracking-widest text-white/35 mb-2.5">{L("SUMMARY", "RESUMEN")}</div>
            <p className="text-white/70 font-light leading-relaxed">{svc.summary}</p>
          </div>
        )}

        <div className="mt-9">
          <div className="font-mono text-[10px] tracking-widest text-white/35 mb-3 flex items-center gap-2"><FileText size={12} />{L("EVIDENCE", "EVIDENCIA")} ({media.length})</div>
          {media.length === 0 ? (
            <p data-testid="report-no-media" className="text-white/35 text-sm font-light rounded-sm p-5" style={{ border: "1px solid rgba(255,255,255,.08)" }}>{L("No media evidence attached to this report yet.", "Aún no hay evidencia adjunta a este reporte.")}</p>
          ) : (
            <div data-testid="report-media" className="grid sm:grid-cols-2 gap-3">
              {media.map((m, i) => (
                <div key={i} data-testid={`report-media-${i}`} className="relative overflow-hidden rounded-sm" style={{ border: "1px solid rgba(255,255,255,.08)", aspectRatio: "4/3", background: "#0d0d0e" }}>
                  <div className="absolute inset-0 flex items-center justify-center text-white/25 text-[10px] tracking-widest font-mono">{L("MEDIA UNAVAILABLE", "MEDIA NO DISPONIBLE")}</div>
                  {m.type === "video"
                    ? <video src={mediaUrl(m.path)} controls className="relative w-full h-full object-cover" />
                    : <img src={mediaUrl(m.path)} alt={m.filename} className="relative w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-10 flex items-center gap-2 text-white/35 text-xs font-light"><ShieldCheck size={14} style={{ color: GOLD }} />{L("Private to your account · shared only with you.", "Privado de tu cuenta · compartido solo contigo.")}</div>
      </div>
    </div>
  );
}

function Center({ children }) {
  return <div className="grain min-h-screen flex items-center justify-center text-white/60" style={{ background: "#0a0a0b" }}>{children}</div>;
}
